import { useState, useEffect } from "react";
import { Box, Button, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { formatCurrency } from "../../../../utils/formatCurrency";
import { useInvoiceSchedules } from "../../../../api/moloni/useInvoiceSchedules";
import { InvoiceScheduleDialog } from "./InvoiceScheduleDialog";
import { InvoiceSchedule, OrderItemForScheduling } from "../types";
import { calculateScheduledTotal, calculatePendingScheduledTotal } from "./utils";
import { InvoiceScheduleRow } from "./InvoiceScheduleRow";
import { useStore } from "../../../../store/store";

type InvoiceSchedulesTableProps = {
    orderId: number;
    treatmentId: number;
    orderTotal: number;
    orderItems: Array<OrderItemForScheduling>;
    invoicedTotal: number | null;
};

export const InvoiceSchedulesTable = ({
    orderId,
    treatmentId,
    orderTotal,
    orderItems,
    invoicedTotal,
}: InvoiceSchedulesTableProps) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const { data: schedules, isLoading } = useInvoiceSchedules(orderId, true);
    const { invoiceSchedulesDialog } = useStore();

    const scheduledTotal = calculateScheduledTotal(schedules);
    const pendingScheduledTotal = calculatePendingScheduledTotal(schedules);
    const totalMatches = Math.abs(scheduledTotal - orderTotal) < 0.01;
    const totalColor = totalMatches ? "success.main" : "error.main";

    useEffect(() => {
        if (invoiceSchedulesDialog.shouldOpen) {
            setIsDialogOpen(true);
            invoiceSchedulesDialog.resetTrigger();
        }
    }, [invoiceSchedulesDialog.shouldOpen, invoiceSchedulesDialog]);

    const handleAddSchedule = () => {
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
    };

    const emptyMessage = "Não existem agendamentos para esta compra";

    return (
        <Box mt={3} ml={0.5} p={2} sx={{ backgroundColor: "#fff" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" pt={2} pb={3}>
                <Box>
                    <Typography variant="h5">Agendamento de Faturas:</Typography>
                    <Typography variant="h6" sx={{ color: totalColor, mt: 1 }}>
                        Total Agendado: {formatCurrency(scheduledTotal)}
                    </Typography>
                </Box>
                <Box display="flex" gap={2} alignItems="center">
                    <Button variant="contained" onClick={handleAddSchedule}>
                        Adicionar Agendamento
                    </Button>
                </Box>
            </Box>

            {isLoading ? (
                <LinearProgress />
            ) : schedules && schedules.length > 0 ? (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell width={50} />
                                <TableCell>Data Agendada</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell>Número de Fatura</TableCell>
                                <TableCell align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {schedules.map((schedule: InvoiceSchedule) => (
                                <InvoiceScheduleRow
                                    key={schedule.id}
                                    schedule={schedule}
                                    orderId={orderId}
                                    treatmentId={treatmentId}
                                    orderTotal={orderTotal}
                                    pendingScheduledTotal={pendingScheduledTotal}
                                    invoicedTotal={invoicedTotal}
                                    orderItems={orderItems}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Box py={4} display="flex" justifyContent="center">
                    <Typography color="text.secondary">{emptyMessage}</Typography>
                </Box>
            )}
            
            <InvoiceScheduleDialog
                isOpen={isDialogOpen}
                orderId={orderId}
                treatmentId={treatmentId}
                onClose={handleCloseDialog}
                orderTotal={orderTotal}
                pendingScheduledTotal={pendingScheduledTotal}
                invoicedTotal={invoicedTotal}
                orderItems={orderItems}
            />
        </Box>
    );
};
